import React, { useState, useEffect } from 'react'
import {Heart, MessageCircleDashed as MessageCircle, Send, X} from 'lucide-react'
import { useComments } from '../hooks/useComments'
import { useLikes } from '../hooks/useLikes'
import { useUserProfile } from '../hooks/useUserProfile'

interface MediaCommentsProps {
  projectId: string
  mediaIndex: number
  mediaUrl?: string
  mediaType?: 'image' | 'video'
  mediaCaption?: string
  currentUserId?: string
  onClose: () => void
}

export function MediaComments({ projectId, mediaIndex, mediaUrl, mediaType, mediaCaption, currentUserId, onClose }: MediaCommentsProps) {
  const { comments, loading: commentsLoading, addComment, deleteComment } = useComments(projectId, mediaIndex)
  const { likes, isLiked, likeCount, loading: likesLoading, toggleLike } = useLikes(projectId, mediaIndex, currentUserId)
  const { profile, fetchUserProfile, canInteract } = useUserProfile(currentUserId || '')
  const [commentText, setCommentText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleLike = async () => {
    if (!currentUserId) {
      setError('Please sign in to like')
      return
    }

    if (!canInteract()) {
      setError('Subscribe to unlock like and comment features ($5/month)')
      return
    }

    try {
      await toggleLike(currentUserId)
      setError('')
    } catch (err) {
      console.error('Failed to toggle like:', err)
    }
  }

  const handleSubmitComment = async () => {
    if (!currentUserId) {
      setError('Please sign in to comment')
      return
    }

    if (!canInteract()) {
      setError('Subscribe to unlock like and comment features ($5/month)')
      return
    }

    if (!commentText.trim()) {
      setError('Please enter a comment')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      await addComment({
        projectId,
        mediaIndex,
        userId: currentUserId,
        userName: profile?.userName || 'Anonymous',
        userProfileImageUrl: profile?.profileImageUrl,
        content: commentText.trim()
      })
      setCommentText('')
    } catch (err: any) {
      setError(err.message || 'Failed to submit comment')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (typeof commentId !== 'string') return
    try {
      await deleteComment(commentId)
    } catch (err) {
      console.error('Failed to delete comment:', err)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0E27] border border-white/10 rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-xl font-bold text-white">Post Details</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Full Media Display */}
          {mediaUrl && (
            <div className="bg-black/30">
              {mediaType === 'image' ? (
                <img
                  src={mediaUrl}
                  alt={mediaCaption || 'Media'}
                  className="w-full h-auto max-h-[400px] object-contain"
                />
              ) : (
                <video
                  src={mediaUrl}
                  controls
                  className="w-full h-auto max-h-[400px] object-contain"
                />
              )}
              {mediaCaption && (
                <div className="p-4 bg-white/5 border-b border-white/10">
                  <p className="text-gray-300">{mediaCaption}</p>
                </div>
              )}
            </div>
          )}

          {/* Like Section */}
          <div className="p-4 border-b border-white/10">
          <button
            onClick={handleLike}
            disabled={likesLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:border-[#0052FF] hover:bg-white/10 transition-all group"
          >
            <Heart
              className={`w-5 h-5 transition-colors ${
                isLiked(currentUserId || '')
                  ? 'fill-red-500 text-red-500'
                  : 'text-gray-400 group-hover:text-red-500'
              }`}
            />
            <span className="text-white font-medium">
              {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
            </span>
          </button>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {commentsLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]"></div>
              <p className="text-gray-400 mt-2">Loading comments...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400">No comments yet. Be the first!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div
                key={comment._id}
                className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-lg"
              >
                {/* User Avatar */}
                {comment.userProfileImageUrl ? (
                  <img
                    src={comment.userProfileImageUrl}
                    alt={comment.userName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0052FF] to-[#60A5FA] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">
                      {comment.userName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Comment Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-white font-semibold text-sm">{comment.userName}</h4>
                    {comment.createdAt && (
                      <span className="text-gray-500 text-xs">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-300 text-sm break-words">{comment.content}</p>
                  
                  {/* Delete button (only for comment owner) */}
                  {currentUserId === comment.userId && (
                    <button
                      onClick={() => comment._id && handleDeleteComment(comment._id)}
                      className="text-red-400 text-xs hover:text-red-300 mt-2"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Comment Input */}
        <div className="p-4 border-t border-white/10">
          {error && (
            <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <div className="flex gap-2">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !submitting && handleSubmitComment()}
              placeholder="Write a comment..."
              className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
            />
            <button
              onClick={handleSubmitComment}
              disabled={submitting || !commentText.trim()}
              className="px-4 py-2 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  )
}
